import React, { useEffect, useId } from "react";
import { UsersIcon } from "lucide-react";
import { Student } from "@/types/Student";
import { GridPagination } from "./GridPagination";
import { StudentCard } from "./StudentCard";
import { StudentCardSkeleton } from "./StudentCardSkeleton";
import { useStudentGridLayout } from "./useStudentGridLayout";
import { EmptyStateComponent } from "../EmptyStateComponent";
import { useTranslation } from "react-i18next";
import { useMultiSidebar } from "../ui/MultiSidebar";

interface StudentGridProps {
  students?: Partial<Student>[];
  isLoading?: boolean;
  numRowsPerPage?: number;
  page: number;
  setPage?: (page: number) => void;
  setPerPage: (perPage: number) => void;
  totalPages: number;
  isError?: boolean;
  hasActiveFilters?: boolean;
  profileSource?: string;
}

export const StudentGrid = ({
  students = [],
  isLoading = false,
  numRowsPerPage = 3,
  page,
  setPage,
  setPerPage,
  totalPages,
  isError = false,
  hasActiveFilters = false,
  profileSource,
}: StudentGridProps) => {
  const { containerRef, cardsPerRow } = useStudentGridLayout();
  const { t } = useTranslation();

  useEffect(() => {
    if (cardsPerRow) setPerPage(cardsPerRow * numRowsPerPage);
  }, [cardsPerRow, setPerPage, numRowsPerPage]);

  const gridId = useId();
  const gridLabelId = useId();
  const { setOpen } = useMultiSidebar("filter");

  // Determine the appropriate empty state message
  const getEmptyStateMessage = () => {
    if (isError) {
      return t("studentCard.gridView.errorStateTitle");
    }
    if (hasActiveFilters) {
      return t("studentCard.gridView.emptyFilterStateTitle");
    }
    return t("studentCard.gridView.emptyStateTitle");
  };

  return (
    <div className="w-full" ref={containerRef}>
      {students.length === 0 && !isLoading ? (
        <EmptyStateComponent
          icon={<UsersIcon />}
          message={getEmptyStateMessage()}
        />
      ) : (
        <>
          <h2 id={gridLabelId} className="sr-only">
            {t("studentList.gridView")}
          </h2>
          <div
            id={gridId}
            role="grid"
            aria-labelledby={gridLabelId}
            aria-rowcount={
              (isLoading
                ? (cardsPerRow || 0) * numRowsPerPage
                : students.length) + 1
            }
            className="grid grid-flow-row-dense auto-rows-auto grid-cols-[repeat(auto-fill,minmax(260px,1fr))] justify-items-start gap-4"
          >
            {isLoading
              ? Array.from({ length: (cardsPerRow || 0) * numRowsPerPage }).map(
                  (_, i) => (
                    <div
                      role="row"
                      key={i}
                      className="w-full max-w-[350px]"
                      aria-rowindex={i + 1}
                    >
                      <StudentCardSkeleton />
                    </div>
                  ),
                )
              : students.map((student, idx) => (
                  <div
                    role="row"
                    key={student.id || idx}
                    className="w-full max-w-[350px]"
                    aria-rowindex={idx + 1}
                  >
                    <div role="gridcell" className="h-full">
                      <StudentCard
                        student={student}
                        onClick={() => setOpen(false)}
                        profileSource={profileSource}
                      />
                    </div>
                  </div>
                ))}
          </div>

          <GridPagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            gridId={gridId}
            isLoading={isLoading}
          />
        </>
      )}
    </div>
  );
};
